import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { TokenService } from '@/pages/auth/token.service';

/**
 * Keeps the session alive during idle stretches (e.g. a long LeadSquared migration setup where the
 * admin fills the form for many minutes without firing requests). The backend uses a SLIDING session:
 * any authed request past the refresh window (jwt.refresh-after-ms, ~15 min) hands back a fresh token
 * via the `X-Refresh-Token` header, which the auth interceptor stores. Normal activity refreshes on
 * its own; this timer covers pure idle by pinging `/auth/ping` (a 200-for-any-role endpoint) every
 * few minutes, so the token never lapses to a 401. No-ops while logged out.
 */
@Injectable({ providedIn: 'root' })
export class SessionKeepAliveService {

    /** Well inside the ~15-min refresh window, so a ping always lands in it before the token expires. */
    private readonly INTERVAL_MS = 5 * 60 * 1000;
    private timer: any = null;

    constructor(private api: ApiService, private token: TokenService) {}

    /** Start the keep-alive loop (idempotent) — the top bar calls this once it mounts, for every role. */
    start(): void {
        if (this.timer) return;
        this.timer = setInterval(() => this.ping(), this.INTERVAL_MS);
    }

    stop(): void {
        if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }

    private ping(): void {
        // Skip while logged out; a fired ping goes through the auth interceptor, which reads any
        // X-Refresh-Token off the 200 response and updates the stored access token.
        if (!this.token.getAccessToken()) return;
        this.api.get<any>('/auth/ping').subscribe({ next: () => {}, error: () => {} });
    }
}
